import { Ai } from './api/ai.js'
import { Limits } from './api/limits.js'
import { Orgs } from './api/orgs.js'
import { TicketMoments } from './api/ticket-moments.js'
import { OrgEvents } from './api/org-events.js'
import { Brand } from './api/brand.js'
import { Meetings, Webhooks } from './api/meetings.js'
import { Rooms } from './api/rooms.js'
import { Videos } from './api/videos.js'
import { Auth } from './auth.js'
import { Capture } from './capture/capture.js'
import { HttpClient } from './http.js'
import { Assistant } from './meeting/assistant.js'
import { MeetingBoard } from './meeting/board.js'
import { Call } from './meeting/call.js'
import { Captions } from './meeting/captions.js'
import { Chat } from './meeting/chat.js'
import { DEFAULT_EMOJI_BASE } from './meeting/emoji.js'
import { Media, type MediaOptions } from './media/media.js'
import { Recording } from './recording/recording.js'
import { Room } from './room.js'
import { SocketManager } from './socket.js'
import { Stage } from './stage/stage.js'
import { resolveStorage, type StorageOptions } from './storage.js'
import { SystemCheck } from './system-check.js'

/**
 * One client per app, one server per client. Everything else hangs off it,
 * so two clients (two servers, or a test beside the app) never share a
 * token, a socket or a meeting.
 */

export interface ClientOptions {
  /** Origin of the server, e.g. `https://meet.example.com`. Default: this page's origin. */
  baseUrl?: string
  /** Browser storage to use; see StorageOptions. */
  storage?: StorageOptions
  /** A fetch to use instead of the global one. */
  fetch?: typeof fetch
  /** How to name the server in connection errors, e.g. a dev proxy's target. Default: the base URL. */
  serverLabel?: string
  /** How the media plane connects; see MediaOptions. */
  media?: MediaOptions
  /** Where the animated reaction emoji are served from. Default: Google Fonts' copy of Noto. */
  emojiBase?: string
}

export class LiveStreamClient {
  readonly http: HttpClient
  readonly auth: Auth
  readonly sockets: SocketManager
  readonly room: Room
  /** The camera, microphone, screen share, and everyone else's media. */
  readonly media: Media
  /** The meeting page's orchestration: joining from the green room, and keeping our media true once in. */
  readonly call: Call
  /** This client's own view of the stage: layout, pin, scene. */
  readonly stage: Stage
  /** The meeting's chat and reactions. */
  readonly chat: Chat
  /** Live captions: sending this person's voice, and showing what comes back. */
  readonly captions: Captions
  /** The in-meeting assistant: catch-ups, questions, live action items. */
  readonly assistant: Assistant
  /** A team's Jira board in the meeting: your own tickets, and for a host who leads the team, the whole team. */
  readonly board: MeetingBoard
  /** Recording the meeting, and finishing uploads a closed tab left behind. */
  readonly recording: Recording
  /** Rooms, their recordings and their meetings. */
  readonly rooms: Rooms
  /** Past meetings: transcripts, notes, exports. */
  readonly meetings: Meetings
  /** A room's outgoing webhooks. */
  readonly webhooks: Webhooks
  /** AI settings, budgets and usage. */
  readonly ai: Ai
  /** Usage limits: a person's month, and an admin's say over them. */
  readonly limits: Limits
  /** The company route: organisations, teams, invites and the connector marketplace. */
  readonly orgs: Orgs
  /** Where tickets were talked about in a past meeting, and the links to those moments in Jira. */
  readonly ticketMoments: TicketMoments
  /** The signed-in person's brand kit: logo, colours and title font. */
  readonly brand: Brand
  /** The signed-in person's videos, and any video by its share link. */
  readonly videos: Videos
  /** Recording a video: screen, camera, or both, with the bubble. */
  readonly capture: Capture
  readonly systemCheck: SystemCheck

  constructor(opts: ClientOptions = {}) {
    const storage = resolveStorage(opts.storage)
    this.http = new HttpClient({
      ...(opts.baseUrl !== undefined ? { baseUrl: opts.baseUrl } : {}),
      ...(opts.fetch ? { fetch: opts.fetch } : {}),
    })
    this.auth = new Auth(this.http, storage.local)
    this.sockets = new SocketManager({
      baseUrl: this.http.baseUrl,
      storage: storage.session,
      deviceStorage: storage.local,
    })
    this.room = new Room(this.http, this.auth, this.sockets, storage.session)
    this.media = new Media(this.http, this.room, storage.local, opts.media)
    this.stage = new Stage(storage.local)
    this.chat = new Chat(this.room, storage.local, opts.emojiBase ?? DEFAULT_EMOJI_BASE)
    this.captions = new Captions(this.room, this.media, storage.local)
    this.assistant = new Assistant(this.room)
    this.board = new MeetingBoard(this.room)
    this.recording = new Recording(this.http, this.auth, this.room, this.media, this.stage)
    this.rooms = new Rooms(this.http)
    this.call = new Call(this.rooms, this.auth, this.room, this.media)
    this.meetings = new Meetings(this.http, this.rooms, this.auth.signedIn)
    this.webhooks = new Webhooks(this.http)
    this.ai = new Ai(this.http)
    this.limits = new Limits(this.http)
    this.orgs = new Orgs(this.http, storage.local, new OrgEvents(this.http))
    this.ticketMoments = new TicketMoments(this.http)
    this.brand = new Brand(this.http)
    this.videos = new Videos(this.http, storage.local, this.auth.signedIn)
    this.capture = new Capture(this.videos, storage.local)
    // Its own connection, anonymous and remembering nothing: a meeting that
    // reused it would join with no identity, and its hello would overwrite
    // the meeting's resume credentials.
    this.systemCheck = new SystemCheck(
      this.http,
      new SocketManager({ baseUrl: this.http.baseUrl, storage: null }),
      opts.serverLabel ?? (this.http.baseUrl || 'this site'),
    )
  }
}

export function createClient(opts: ClientOptions = {}): LiveStreamClient {
  return new LiveStreamClient(opts)
}
